<template>
  <VDialog
    v-model="show"
    max-width="600px"
    :transition="false"
    attach="#app"
    data-testid="emergency-consultation-dialog"
  >
    <VCard class="p-4">
      <VCardTitle class="flex justify-space-between items-center p-0">
        <div class="flex items-center gap-2 text-h4 text-secondary p-4">
          <VIcon color="secondary" size="24" icon="emergency" class="material-icons-round" />
          {{ t('Emergency Call') }}
        </div>
        <VBtn
          icon="close"
          variant="plain"
          class="button-small mr-2"
          size="small"
          :ripple="false"
          data-testid="btn-close"
          @click="close"
        />
      </VCardTitle>
      <VCardText class="p-4">
        <div class="text-subtitle-1">{{ t('Patient') }}</div>
        <PatientAutocomplete
          v-model="selectedCaseId"
          :urgency-type="UrgencyDTO.EMERGENCY"
          disable-on-hospital-mismatch
          data-testid="patient-autocomplete"
        />
      </VCardText>
      <template #actions>
        <VCardActions>
          <VBtn
            variant="text"
            color="primary"
            size="large"
            data-testid="btn-cancel"
            class="ml-auto button-large"
            :text="t('Cancel')"
            @click="close"
          />
          <VBtn
            variant="elevated"
            color="primary"
            size="large"
            class="ml-4 button-large"
            :text="t('Start emergency call')"
            data-testid="btn-confirm"
            :loading="createEmergencyInProgress"
            :disabled="!selectedCaseId"
            @click="confirmAndClose"
          />
        </VCardActions>
      </template>
    </VCard>
  </VDialog>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, watch } from 'vue'
import PatientAutocomplete from '@/components/patient/components/atoms/PatientAutocomplete.vue'
import { ConsultationService } from '@/services/telehealthApi/ConsultationService'
import { NoticeType } from '@/stores/models/Notice'
import { useNoticeStore } from '@/stores/notice'
import { useCallStore } from '@/stores/call'
import { UrgencyDTO } from '@/generated'

const show = ref<boolean>(false)
const selectedCaseId = ref<string | undefined>()
const createEmergencyInProgress = ref<boolean>(false)

const { t } = useI18n()
const noticeStore = useNoticeStore()

async function confirmAndClose() {
  try {
    createEmergencyInProgress.value = true
    const createdRequestId = await ConsultationService.createEmergencyRequest(selectedCaseId.value!)
    // The confirm button already shows progress, so the call frame skips its own loader.
    await useCallStore().joinCall(createdRequestId, { showJoiningLoader: false })
    close()
  } catch (error) {
    console.error((error as Error).message)
    noticeStore.add({
      type: NoticeType.ERROR,
      message: t('Failed to submit consultation request')
    })
  } finally {
    createEmergencyInProgress.value = false
  }
}

function open(caseId?: string) {
  show.value = true
  selectedCaseId.value = caseId
}

function close() {
  show.value = false
}

function clear() {
  selectedCaseId.value = undefined
}

watch(show, () => {
  if (!show.value) clear()
})

defineExpose({
  open
})
</script>
